import React, { Fragment, useEffect, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { useNavigate } from 'react-router-dom';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import account_icon from 'assets/user_account.svg';
import BuyTokenButton from 'components/BuyTokenButton';
import useModalState from 'hooks/useModalState';
import ls from 'local-storage';
import { useMappedState } from 'redux-react-hook';
import { USER_ROUTES } from 'routes/routes';
import styled from 'styled-components';
import { isBlank } from 'utils/storeUtils';

import burger_toggle_img from '../../assets/burger_toggle.svg';
import crown_img from '../../assets/crown.svg';
import crown_grad_img from '../../assets/crown_grad.svg';
import e_logo from '../../assets/E_Logo.svg';
import CommonHelper from '../../helpers/CommonHelper';
import { AccountMenu } from '../hero/AccountMenu';

export const HeaderPanel = props => {
  const {
    isSticky = false,
    isPromotionBanner,
    promotionMode,
    onSignIn = () => { },
    onSignUp = () => { },
    onPremiumClick = () => { },
    onContactClick = () => { }
  } = props;

  const navigate = useNavigate();

  const { currentUser, hasSubscription, isSignedIn } = useMappedState(
    state => {
      const currentUserId = state.users.currentUserId;
      const currentUser = state.users.usersMap.get(currentUserId);
      const hasSubscription = currentUser?.hasSubscription == true;
      const isSignedIn = currentUserId != undefined;
      return {
        currentUser: currentUser,
        hasSubscription: hasSubscription,
        isSignedIn: isSignedIn
      };
    });

  const [isSidebarOpen, openSidebar, closeSidebar] = useModalState();
  const [sticky, setSticky] = useState(false);

  useEffect(() => {
    const element = document.getElementById('header');

    const handleScroll = () => {
      setSticky(window.scrollY > element?.offsetHeight);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const PromotionBannerComponent = promotionMode.banner.bannerComponent;

  return (
    <>
      {isPromotionBanner && <PromotionBannerComponent onClick={onPremiumClick} mode={promotionMode} />}
      <TopNavPlaceholder
        id="header"
        topPosition={isPromotionBanner ? '60px' : 'none'}
        style={{
          backgroundColor: sticky ? 'rgba(16, 13, 11, 1)' : 'transparent',
          transition: 'all 0.5s',
          position: isSticky ? 'fixed' : 'relative'
        }}
      >
        <TopNavRight>
          <BurgerImg
            alt={'menu'}
            src={burger_toggle_img}
            onClick={openSidebar}
          />
          <Logo
            alt={'logo'}
            src={e_logo}
            onClick={() => navigate('/')}
          />
        </TopNavRight >

        <TopNavRight>
          {!hasSubscription && (
            <PremiumButton
              className={'md:mr-4'}
              onClick={() => {
                onPremiumClick();
              }}
            >
              <img className={'mr-2'} style={{ height: '24px' }} src={crown_img} />
              Premium
            </PremiumButton>
          )}

          {hasSubscription && isMobile && (
            <BuyTokenButton isMobile onPremiumClick={onPremiumClick} />
          )}

          <DesktopVisibleTopPlaceholder>
            {!isSignedIn && (
              <>
                <OrangeOutlineButton
                  style={{ height: '48px' }}
                  className={'mr-4'}
                  onClick={() => {
                    onSignUp();
                  }}
                >
                  Create account
                </OrangeOutlineButton>

                <OrangeButton
                  style={{ height: '48px' }}
                  onClick={onSignIn}
                >
                  Sign in
                </OrangeButton>
              </>
            )}

            {isSignedIn &&
              (
                <>
                  {hasSubscription && <BuyTokenButton onPremiumClick={onPremiumClick} />}
                  <AccountMenu />
                </>
              )}
          </DesktopVisibleTopPlaceholder >
        </TopNavRight >
      </TopNavPlaceholder >

      <Transition.Root show={isSidebarOpen} as={Fragment}>
        <Dialog as="div" className="relative z-40 md:hidden" onClose={closeSidebar}>
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0" style={{ backdropFilter: 'blur(2px)' }} />
          </Transition.Child>

          <div className="fixed inset-0 z-40 flex">
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <Dialog.Panel className="relative flex w-full max-w-[240px] flex-1 flex-col bg-[rgb(29,29,29)]">
                <Transition.Child
                  as={Fragment}
                  enter="ease-in-out duration-300"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="ease-in-out duration-300"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <div className="absolute top-0 left-0 pt-6 pl-6">
                    <button
                      type="button"
                      className="flex h-8 w-8 items-center justify-center rounded-full focus:outline-none focus:ring-0" //focus:ring-inset focus:ring-white"
                      onClick={closeSidebar}
                    >
                      <span className="sr-only">Close sidebar</span>
                      <XMarkIcon className="h-8 w-8 stroke-2 text-white" aria-hidden="true" />
                    </button>
                  </div>
                </Transition.Child>
                <div className="h-0 flex-1 overflow-y-auto pt-5 pb-4">
                  <SideNav className="flex h-full flex-col justify-between space-y-1 px-6 pt-16 text-white">
                    <div>
                      {isBlank(currentUser) ? (
                        <>
                          <SideNavItem
                            className={'my-2 border-b border-b-[rgb(104,104,104)] py-5'}
                            onClick={() => {
                              onSignIn();
                              closeSidebar();
                            }}
                          >
                            Sign In
                          </SideNavItem>

                          <SideNavItem
                            className={'my-2 border-b border-b-[rgb(104,104,104)] py-5'}
                            onClick={() => {
                              onSignUp();
                              closeSidebar();
                            }}
                          >
                            Create account
                          </SideNavItem>
                        </>
                      ) : (
                        <>
                          <MyProfileSideNavItem
                            className={'my-2 border-b border-b-[rgb(104,104,104)] py-5'}
                            onClick={() => {
                              navigate(USER_ROUTES.PROFILE());
                              closeSidebar();
                            }}
                          >
                            <img src={account_icon} alt='Profile' />
                            <ProfileWrapper>
                              My profile
                              {hasSubscription && <BuyTokenButton isChip />}
                            </ProfileWrapper>
                          </MyProfileSideNavItem>
                          <SideNavItem
                            className={'my-2 border-b border-b-[rgb(104,104,104)] py-5'}
                            onClick={() => {
                              navigate(USER_ROUTES.HISTORY());
                              closeSidebar();
                            }}
                          >
                            History
                          </SideNavItem>

                          <SideNavItem
                            className={'my-2 border-b border-b-[rgb(104,104,104)] py-5'}
                            onClick={() => {
                              ls.remove('token');
                              navigate('/');
                              window.location.reload();
                            }}
                          >
                            Log out
                          </SideNavItem>
                        </>
                      )}

                      {!hasSubscription && (
                        <SideNavItem
                          className={
                            'my-2 flex flex-row items-center border-b border-b-[rgb(104,104,104)] py-5'
                          }
                          onClick={() => {
                            onPremiumClick();
                            CommonHelper.yandexTrackEvent(`header-buy-premium`);
                          }}
                        >
                          <img src={crown_grad_img} className={'mr-2'} />
                          <span>Buy premium</span>
                        </SideNavItem>
                      )}
                    </div>

                    <SideNavItem
                      className="pb-6"
                      onClick={() => {
                        onContactClick();
                        closeSidebar();
                      }}
                    >
                      Feedback
                    </SideNavItem>
                  </SideNav>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );
};


const DesktopVisibleTopPlaceholder = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  @media (max-width: 768px) {
    display: none;
  }
`;

const BurgerImg = styled.img`
  display: none;
  height: 28px;
  margin-right: 16px;
  cursor: pointer;
  @media (max-width: 768px) {
    display: block;
  }
`;

const PremiumButton = styled.div`
  cursor: pointer;
  color: white;
  font-size: 18px;
  line-height: 24px;
  font-weight: 700;
  height: 48px;
  padding-left: 32px;
  padding-right: 32px;
  border-radius: 1000px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  background: linear-gradient(90deg, #9e5eee 0%, #b759ea 32%, #c65de9 66%, #b885f4 100%);

  @media (max-width: 768px) {
    height: 36px;
    padding-left: 15px;
    padding-right: 15px;
    font-size: 16px;
  }
`;

const Logo = styled.img`
  height: 40px;
  cursor: pointer;
  @media (max-width: 768px) {
    height: 26px;
  }
`;

const TopNavRight = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const TopNavPlaceholder = styled.div`
  max-width: 100%;
  width: 100%;
  padding: 0 80px;
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  height: 96px;
  /* position: sticky; */
  top: ${({ topPosition }) => topPosition || '0'};
  z-index: 10;

  @media (max-width: 1400px) {
    width: 100%;
  }

  @media (max-width: 900px) {
    height: 76px;
    padding: 0 32px;
  }

  @media (max-width: 768px) {
    height: 76px;
    padding: 0 24px;
  }
`;

const OrangeOutlineButton = styled.div`
  cursor: pointer;
  color: rgba(255, 126, 85, 1);
  border: 1px solid rgba(255, 126, 85, 1);
  font-size: 18px;
  line-height: 24px;
  height: 64px;
  padding-left: 32px;
  padding-right: 32px;
  border-radius: 1000px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  font-weight: 700;

  :hover {
    background: rgba(255, 126, 85, 1);
    color: white;
  }
`;

const OrangeButton = styled.div`
  cursor: pointer;
  background: rgba(255, 126, 85, 1);
  color: white;
  font-size: 18px;
  line-height: 24px;
  height: 64px;
  padding-left: 32px;
  padding-right: 32px;
  border-radius: 1000px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  font-weight: 700;

  @media (max-width: 720px) {
    height: 56px;
  }
`;

const SideNavItem = styled.div`
  font-family: Nunito Sans, sans-serif;
  font-optical-sizing: auto;
  font-size: 18px;
  font-weight: 700;
`;

const MyProfileSideNavItem = styled(SideNavItem)`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
`;

const ProfileWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
`;

const SideNav = styled.nav`
  color: white;
`;
